/** Formatting for es-MX staff screens. Money is integer centavos everywhere (contracts/common.ts). */

const TZ = "America/Mexico_City";

const moneyFmt = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
const dateFmt = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, day: "numeric", month: "short", year: "numeric" });
const dateNumericFmt = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric" });
const dateLongFmt =new Intl.DateTimeFormat("es-MX", { timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric" });
const timeFmt = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
const dateTimeFmt = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export function money(cents: number): string {
  return moneyFmt.format(cents / 100);
}

/** "1234.5" → 123450. Accepts commas and a leading $. */
export function parseMoney(input: string): number | null {
  const clean = input.replace(/[$,\s]/g, "");
  if (!clean) return null;
  const n = Number(clean);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function fmtDate(iso: string): string {
  return dateFmt.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso));
}
/** A calendar date (`YYYY-MM-DD`, e.g. a birth date) as es-MX `dd/mm/aaaa`; no time zone involved. */
export function fmtDayNumeric(isoDay: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDay);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : isoDay;
}
/** "02/11/2026": the dd/mm/aaaa the laboratory reads in its visit list (Toronja's history did the same). */
export function fmtDateNumeric(iso: string): string {
  return dateNumericFmt.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso));
}
export function fmtDateLong(d: Date): string {
  return dateLongFmt.format(d);
}
export function fmtTime(iso: string): string {
  return timeFmt.format(new Date(iso));
}
export function fmtDateTime(iso: string): string {
  return dateTimeFmt.format(new Date(iso));
}

/** Today's date in the lab's time zone as YYYY-MM-DD. */
export function todayIso(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Local midnight (Mexico City) of a YYYY-MM-DD as ISO instants [start, end). */
export function dayBounds(day: string): { start: string; end: string } {
  // Mexico City has no DST since 2022: fixed UTC−6.
  const start = new Date(`${day}T00:00:00-06:00`);
  const end = new Date(start.getTime() + 86_400_000);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** "27 años", "8 meses", "12 días" at a reference date (default today). */
export function ageText(birthDate: string, at: Date = new Date()): string {
  const b = new Date(`${birthDate}T12:00:00`);
  let years = at.getFullYear() - b.getFullYear();
  let months = at.getMonth() - b.getMonth();
  if (at.getDate() < b.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years >= 2) return `${years} años`;
  if (years === 1) return months > 0 ? `1 año ${months} m` : "1 año";
  if (months >= 1) return `${months} ${months === 1 ? "mes" : "meses"}`;
  const days = Math.max(0, Math.floor((at.getTime() - b.getTime()) / 86_400_000));
  return `${days} ${days === 1 ? "día" : "días"}`;
}

export function sexLabel(sex: "M" | "F"): string {
  return sex === "F" ? "Femenino" : "Masculino";
}

export function fullName(p: { first_name: string; paternal_surname: string; maternal_surname?: string | null }): string {
  return [p.first_name, p.paternal_surname, p.maternal_surname].filter(Boolean).join(" ");
}

export function initials(p: { first_name: string; paternal_surname: string }): string {
  return `${p.first_name[0] ?? ""}${p.paternal_surname[0] ?? ""}`.toUpperCase();
}

/** 10-digit MX number as "442 123 4567". */
export function fmtPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const d = phone.replace(/\D/g, "").slice(-10);
  if (d.length !== 10) return phone;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

export function fmtNumber(n: number, decimals: number | null): string {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 3,
  }).format(n);
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Unspecified-tube text: a study whose catalogue entry names no container still gets a tube. */
export const UNSPECIFIED_TUBE = "Tubo sin especificar";

/**
 * What a tube is called on screen. A sample whose studies have no mapped container (the imported
 * catalogue) still needs a label, so it reads "Tubo sin especificar" (or its sample type, when
 * only that is known) instead of disappearing.
 */
export function tubeName(s: { container_name: string | null; sample_type_name: string | null }): string {
  if (s.container_name) return s.container_name;
  if (s.sample_type_name) return `${s.sample_type_name} · recipiente sin especificar`;
  return UNSPECIFIED_TUBE;
}

/** "hace un momento", "hace 25 min", "hace 3 h", "hace 2 días": how long a tube has waited. */
export function fmtAgo(iso: string, now: Date = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "hace 1 día" : `hace ${days} días`;
}

const dueLongFmt = new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" });
const dueShortFmt = new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" });

/** A promised day (YYYY-MM-DD, lab-local): "jueves 5 de noviembre", or short "jue 5 nov". No time zone involved. */
export function fmtDueDay(isoDay: string, short = false): string {
  return (short ? dueShortFmt : dueLongFmt).format(new Date(`${isoDay}T12:00:00Z`)).replace(/[.,]/g, "");
}
